/**
 * Changing the equipment register, and the record of every change.
 *
 * The register is read by somebody deciding whether a vehicle may go airside,
 * so a change to it is evidence in its own right: who changed the airside
 * permit date, when, from what, and why. Every operation here therefore does
 * two things — it changes the item, and it APPENDS one entry to
 * `register.changes` naming each field that moved, its value before and its
 * value after. Nothing in this module rewrites or removes an entry already in
 * that list; `record` is the only way in, and it only concatenates. The test
 * pins that from every operation.
 *
 * Three rules shape the operations.
 *
 * - The TAG is not editable. It is the fleet number painted on the machine and
 *   the key the work orders and the notes find it by; renumbering in place
 *   would orphan both. A renumbered machine is retired and added again.
 * - Ownership moves only by TRANSFER, which requires a reason. An owner or a
 *   department changed as one more field in an edit is exactly the change a
 *   later reader cannot account for, so the edit form does not carry them.
 * - Nothing is deleted. RETIRE takes an item off the working register and
 *   keeps it, its history and its tag — a tag once used is never reused, so
 *   an old work order can never land on a different machine.
 *
 * The form, the import and the diff all work on a FLAT view of an item — one
 * string per field — so "what changed" is a comparison of two string maps and
 * the round trip item → flat → item is exact (pinned for every sample item).
 */
import {
  CONDITION_LABEL,
  CONDITION_ORDER,
  FLEET_KINDS,
  type Condition,
  type FleetItem,
  type FleetKind,
  type FleetRegister,
} from "./fleet";

/* ── the fields ───────────────────────────────────────────────────────── */

export type FieldKey =
  | "tag"
  | "name"
  | "kind"
  | "make"
  | "model"
  | "year"
  | "registration"
  | "ownerKind"
  | "owner"
  | "department"
  | "usedByDepartment"
  | "condition"
  | "conditionNote"
  | "location"
  | "hoursRun"
  | "driver"
  | "driverPermit"
  | "driverPermitExpiry"
  | "licenceExpiry"
  | "insuranceExpiry"
  | "airsidePermitRef"
  | "airsidePermitExpiry"
  | "inspectionLast"
  | "inspectionNextDue"
  | "inspectionResult";

export type FieldType = "text" | "date" | "number" | "kind" | "condition" | "ownerKind";

export interface FieldDef {
  key: FieldKey;
  /** the English label, which is also the Arabic key */
  label: string;
  /** the column heading in an import file */
  column: string;
  type: FieldType;
  group: "identity" | "ownership" | "state" | "driver" | "dates";
}

export const FIELDS: readonly FieldDef[] = [
  { key: "tag", label: "Tag", column: "tag", type: "text", group: "identity" },
  { key: "name", label: "Name", column: "name", type: "text", group: "identity" },
  { key: "kind", label: "Type", column: "type", type: "kind", group: "identity" },
  { key: "make", label: "Make", column: "make", type: "text", group: "identity" },
  { key: "model", label: "Model", column: "model", type: "text", group: "identity" },
  { key: "year", label: "Year", column: "year", type: "number", group: "identity" },
  { key: "registration", label: "Registration", column: "registration", type: "text", group: "identity" },
  { key: "ownerKind", label: "Owned by", column: "owner_kind", type: "ownerKind", group: "ownership" },
  { key: "owner", label: "Owner", column: "owner", type: "text", group: "ownership" },
  { key: "department", label: "Department", column: "department", type: "text", group: "ownership" },
  {
    key: "usedByDepartment",
    label: "Used by department",
    column: "used_by_department",
    type: "text",
    group: "ownership",
  },
  { key: "condition", label: "Condition", column: "condition", type: "condition", group: "state" },
  { key: "conditionNote", label: "Condition note", column: "condition_note", type: "text", group: "state" },
  { key: "location", label: "Location", column: "location", type: "text", group: "state" },
  { key: "hoursRun", label: "Hours run", column: "hours_run", type: "number", group: "state" },
  { key: "driver", label: "Driver", column: "driver", type: "text", group: "driver" },
  {
    key: "driverPermit",
    label: "Driver's airside driving permit",
    column: "driver_permit",
    type: "text",
    group: "driver",
  },
  {
    key: "driverPermitExpiry",
    label: "Driving permit expiry",
    column: "driver_permit_expiry",
    type: "date",
    group: "driver",
  },
  { key: "licenceExpiry", label: "Vehicle license expiry", column: "licence_expiry", type: "date", group: "dates" },
  { key: "insuranceExpiry", label: "Insurance expiry", column: "insurance_expiry", type: "date", group: "dates" },
  {
    key: "airsidePermitRef",
    label: "Airside vehicle permit",
    column: "airside_permit_ref",
    type: "text",
    group: "dates",
  },
  {
    key: "airsidePermitExpiry",
    label: "Airside permit expiry",
    column: "airside_permit_expiry",
    type: "date",
    group: "dates",
  },
  { key: "inspectionLast", label: "Last safety inspection", column: "inspection_last", type: "date", group: "dates" },
  {
    key: "inspectionNextDue",
    label: "Next safety inspection due",
    column: "inspection_next_due",
    type: "date",
    group: "dates",
  },
  { key: "inspectionResult", label: "Inspection result", column: "inspection_result", type: "text", group: "dates" },
];

export const FIELD: Record<FieldKey, FieldDef> = Object.fromEntries(FIELDS.map((f) => [f.key, f])) as Record<
  FieldKey,
  FieldDef
>;

/** What only a transfer may change. */
export const OWNERSHIP_FIELDS: readonly FieldKey[] = ["ownerKind", "owner", "department", "usedByDepartment"];

/** What the edit form carries: everything but the tag and the ownership. */
export const EDIT_FIELDS: readonly FieldKey[] = FIELDS.map((f) => f.key).filter(
  (k) => k !== "tag" && !OWNERSHIP_FIELDS.includes(k),
);

export const OWNER_KIND_LABEL: Record<"airport" | "stakeholder", string> = {
  airport: "The airport",
  stakeholder: "A stakeholder",
};

export type Flat = Partial<Record<FieldKey, string>>;

/** One string per field; a blank is an empty string, never a placeholder. */
export function flatten(i: FleetItem): Flat {
  const s = (v: string | number | undefined) => (v == null ? "" : String(v));
  return {
    tag: i.tag,
    name: i.name,
    kind: i.kind,
    make: s(i.make),
    model: s(i.model),
    year: s(i.year),
    registration: s(i.registration),
    ownerKind: i.owner.kind,
    owner: i.owner.name,
    department: s(i.department),
    usedByDepartment: s(i.usedByDepartment),
    condition: i.condition,
    conditionNote: s(i.conditionNote),
    location: s(i.location),
    hoursRun: s(i.hoursRun),
    driver: s(i.driver?.name),
    driverPermit: s(i.driver?.permit),
    driverPermitExpiry: s(i.driver?.permitExpiry),
    licenceExpiry: s(i.licenceExpiry),
    insuranceExpiry: s(i.insuranceExpiry),
    airsidePermitRef: s(i.airsidePermit?.ref),
    airsidePermitExpiry: s(i.airsidePermit?.expiry),
    inspectionLast: s(i.safetyInspection?.lastAt),
    inspectionNextDue: s(i.safetyInspection?.nextDueAt),
    inspectionResult: s(i.safetyInspection?.result),
  };
}

/**
 * The item a flat view describes. `base` carries what the flat view does not:
 * the id, the maintenance log and the retirement.
 */
export function toItem(flat: Flat, base: Pick<FleetItem, "id" | "maintenance" | "retired">): FleetItem {
  const v = (k: FieldKey) => (flat[k] ?? "").trim() || undefined;
  const n = (k: FieldKey) => (v(k) === undefined ? undefined : Number(v(k)));
  const item: FleetItem = {
    id: base.id,
    tag: v("tag") ?? "",
    name: v("name") ?? "",
    kind: v("kind") as FleetKind,
    owner: { kind: (v("ownerKind") ?? "airport") as "airport" | "stakeholder", name: v("owner") ?? "" },
    condition: (v("condition") ?? "unknown") as Condition,
    make: v("make"),
    model: v("model"),
    year: n("year"),
    registration: v("registration"),
    department: v("department"),
    usedByDepartment: v("usedByDepartment"),
    conditionNote: v("conditionNote"),
    location: v("location"),
    hoursRun: n("hoursRun"),
    driver: v("driver")
      ? { name: v("driver")!, permit: v("driverPermit"), permitExpiry: v("driverPermitExpiry") }
      : undefined,
    licenceExpiry: v("licenceExpiry"),
    insuranceExpiry: v("insuranceExpiry"),
    airsidePermit:
      v("airsidePermitRef") || v("airsidePermitExpiry")
        ? { ref: v("airsidePermitRef"), expiry: v("airsidePermitExpiry") }
        : undefined,
    safetyInspection:
      v("inspectionLast") || v("inspectionNextDue") || v("inspectionResult")
        ? { lastAt: v("inspectionLast"), nextDueAt: v("inspectionNextDue"), result: v("inspectionResult") }
        : undefined,
    maintenance: base.maintenance,
    retired: base.retired,
  };
  return strip(item);
}

/** Drop undefined keys, nested ones too, so a stored item carries no noise. */
function strip<T extends object>(o: T): T {
  for (const [k, val] of Object.entries(o)) {
    if (val === undefined) delete (o as Record<string, unknown>)[k];
    else if (val && typeof val === "object" && !Array.isArray(val)) strip(val as object);
  }
  return o;
}

/* ── checking what somebody typed ─────────────────────────────────────── */

export interface FieldError {
  field: FieldKey;
  /** English, and an Arabic key */
  message: string;
}

export function isDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

const sameTag = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase();

/**
 * What is wrong with a flat item before it is saved. `selfId` is the item
 * being edited, so its own tag does not count as taken.
 */
export function checkItem(flat: Flat, reg: FleetRegister, selfId?: string, now = new Date()): FieldError[] {
  const errs: FieldError[] = [];
  const v = (k: FieldKey) => (flat[k] ?? "").trim();
  if (!v("tag"))
    errs.push({ field: "tag", message: "A tag is needed: it is how work orders and notes find the item." });
  else if (reg.items.some((i) => i.id !== selfId && sameTag(i.tag, v("tag"))))
    errs.push({
      field: "tag",
      message: "That tag is already on the register. A tag is never reused, retired items included.",
    });
  if (!v("name")) errs.push({ field: "name", message: "A name is needed." });
  if (!FLEET_KINDS.some((k) => k.key === v("kind"))) errs.push({ field: "kind", message: "Choose a type." });
  if (!CONDITION_ORDER.includes(v("condition") as Condition))
    errs.push({ field: "condition", message: "Choose a condition." });
  if (v("ownerKind") !== "airport" && v("ownerKind") !== "stakeholder")
    errs.push({ field: "ownerKind", message: "Say whether the airport or a stakeholder owns it." });
  if (!v("owner")) errs.push({ field: "owner", message: "Name the owner." });
  for (const f of FIELDS)
    if (f.type === "date" && v(f.key) && !isDate(v(f.key)))
      errs.push({ field: f.key, message: "A date is written YYYY-MM-DD." });
  const year = v("year");
  if (year && (!/^\d{4}$/.test(year) || Number(year) < 1950 || Number(year) > now.getUTCFullYear() + 1))
    errs.push({ field: "year", message: "A year of manufacture, four digits." });
  const hours = v("hoursRun");
  if (hours && !(Number(hours) >= 0)) errs.push({ field: "hoursRun", message: "Hours run is a number, zero or more." });
  if (!v("driver") && (v("driverPermit") || v("driverPermitExpiry")))
    errs.push({ field: "driver", message: "A driving permit needs the driver's name." });
  return errs;
}

/* ── the record of changes ────────────────────────────────────────────── */

export type ChangeAction = "added" | "edited" | "transferred" | "retired" | "reinstated" | "imported";

export const ACTION_LABEL: Record<ChangeAction, string> = {
  added: "Added",
  edited: "Edited",
  transferred: "Transferred",
  retired: "Retired",
  reinstated: "Reinstated",
  imported: "Imported",
};

export interface FieldChange {
  field: FieldKey;
  from?: string;
  to?: string;
}

export interface FleetChange {
  id: string;
  /** when the change was saved, ISO */
  at: string;
  by: string;
  itemId: string;
  tag: string;
  action: ChangeAction;
  reason?: string;
  /** for a transfer: the date it takes effect, where that is not the day it was entered */
  effective?: string;
  fields: FieldChange[];
}

export interface Actor {
  by: string;
  /** ISO time of the save */
  at: string;
  /** a fresh id; injected so a test is deterministic */
  newId: () => string;
}

export function diff(before: Flat, after: Flat, keys: readonly FieldKey[] = FIELDS.map((f) => f.key)): FieldChange[] {
  const out: FieldChange[] = [];
  for (const k of keys) {
    const a = (before[k] ?? "").trim();
    const b = (after[k] ?? "").trim();
    if (a !== b) out.push({ field: k, from: a || undefined, to: b || undefined });
  }
  return out;
}

/** The one way into the history: appended, never edited. */
function record(reg: FleetRegister, change: FleetChange): FleetRegister {
  return { ...reg, changes: [...(reg.changes ?? []), change] };
}

function replace(reg: FleetRegister, next: FleetItem): FleetRegister {
  return { ...reg, items: reg.items.map((i) => (i.id === next.id ? next : i)) };
}

function find(reg: FleetRegister, id: string): FleetItem {
  const i = reg.items.find((x) => x.id === id);
  if (!i) throw new Error(`no item ${id} on the register`);
  return i;
}

function need(reason: string | undefined, what: string): string {
  const r = (reason ?? "").trim();
  if (!r) throw new Error(`a reason is needed to ${what}`);
  return r;
}

function guard(flat: Flat, reg: FleetRegister, selfId?: string) {
  const errs = checkItem(flat, reg, selfId);
  if (errs.length) throw new Error(errs.map((e) => `${e.field}: ${e.message}`).join(" "));
}

export function addItem(reg: FleetRegister, flat: Flat, who: Actor, reason?: string): FleetRegister {
  guard(flat, reg);
  const item = toItem(flat, { id: who.newId() });
  const change: FleetChange = {
    id: who.newId(),
    at: who.at,
    by: who.by,
    itemId: item.id,
    tag: item.tag,
    action: "added",
    reason: reason?.trim() || undefined,
    fields: diff({}, flatten(item)),
  };
  return record({ ...reg, items: [...reg.items, item] }, change);
}

/** Edit what the edit form carries. The tag and the ownership are ignored here. */
export function editItem(reg: FleetRegister, id: string, changes: Flat, who: Actor, reason?: string): FleetRegister {
  const before = find(reg, id);
  const was = flatten(before);
  const next: Flat = { ...was };
  for (const k of EDIT_FIELDS) if (k in changes) next[k] = changes[k];
  const fields = diff(was, next, EDIT_FIELDS);
  if (fields.length === 0) return reg;
  guard(next, reg, id);
  return record(replace(reg, toItem(next, before)), {
    id: who.newId(),
    at: who.at,
    by: who.by,
    itemId: id,
    tag: before.tag,
    action: "edited",
    reason: reason?.trim() || undefined,
    fields,
  });
}

export interface Transfer {
  ownerKind: "airport" | "stakeholder";
  owner: string;
  department?: string;
  usedByDepartment?: string;
  /** where it now lives, when the transfer moves it */
  location?: string;
  effective?: string;
}

export function transferItem(reg: FleetRegister, id: string, to: Transfer, who: Actor, reason: string): FleetRegister {
  const why = need(reason, "transfer an item");
  const before = find(reg, id);
  const was = flatten(before);
  const next: Flat = {
    ...was,
    ownerKind: to.ownerKind,
    owner: to.owner,
    department: to.department ?? "",
    usedByDepartment: to.usedByDepartment ?? "",
    ...(to.location !== undefined ? { location: to.location } : {}),
  };
  const fields = diff(was, next, [...OWNERSHIP_FIELDS, "location"]);
  if (fields.length === 0) return reg;
  guard(next, reg, id);
  if (to.effective && !isDate(to.effective)) throw new Error("the effective date is written YYYY-MM-DD");
  return record(replace(reg, toItem(next, before)), {
    id: who.newId(),
    at: who.at,
    by: who.by,
    itemId: id,
    tag: before.tag,
    action: "transferred",
    reason: why,
    effective: to.effective || undefined,
    fields,
  });
}

export function retireItem(reg: FleetRegister, id: string, who: Actor, reason: string): FleetRegister {
  const why = need(reason, "retire an item");
  const before = find(reg, id);
  if (before.retired) return reg;
  return record(replace(reg, { ...before, retired: { at: who.at, by: who.by, reason: why } }), {
    id: who.newId(),
    at: who.at,
    by: who.by,
    itemId: id,
    tag: before.tag,
    action: "retired",
    reason: why,
    fields: [],
  });
}

export function reinstateItem(reg: FleetRegister, id: string, who: Actor, reason: string): FleetRegister {
  const why = need(reason, "reinstate an item");
  const before = find(reg, id);
  if (!before.retired) return reg;
  const { retired: _gone, ...rest } = before;
  return record(replace(reg, rest), {
    id: who.newId(),
    at: who.at,
    by: who.by,
    itemId: id,
    tag: before.tag,
    action: "reinstated",
    reason: why,
    fields: [],
  });
}

/** The changes to one item, newest first. */
export function historyOf(reg: FleetRegister, itemId: string): FleetChange[] {
  return (reg.changes ?? []).filter((c) => c.itemId === itemId).reverse();
}

/** A stored value, as a reader should see it — before translation. */
export function shownValue(field: FieldKey, v: string | undefined): string | undefined {
  if (!v) return undefined;
  const type = FIELD[field].type;
  if (type === "kind") return FLEET_KINDS.find((k) => k.key === v)?.label ?? v;
  if (type === "condition") return CONDITION_LABEL[v as Condition] ?? v;
  if (type === "ownerKind") return OWNER_KIND_LABEL[v as "airport" | "stakeholder"] ?? v;
  return v;
}

/* ── import from a spreadsheet ────────────────────────────────────────── */

/**
 * Rows pasted from Excel arrive tab-separated; a saved CSV arrives with commas,
 * or semicolons where the machine's list separator is one. The first line
 * decides. Quotes follow the CSV convention ("" is a quote inside a field).
 */
export function parseTable(text: string): string[][] {
  const src = text.replace(/^﻿/, "").replace(/\r\n?/g, "\n");
  const first = src.split("\n", 1)[0] ?? "";
  const count = (c: string) => first.split(c).length - 1;
  const sep = first.includes("\t") ? "\t" : count(";") > count(",") ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"' && cell === "") quoted = true;
    else if (c === sep) {
      row.push(cell);
      cell = "";
    } else if (c === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += c;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Which field a column heading means: the template's column name, the key, or the English label. */
export function columnFor(heading: string): FieldKey | null {
  const h = norm(heading);
  if (!h) return null;
  for (const f of FIELDS) if (norm(f.column) === h || norm(f.key) === h || norm(f.label) === h) return f.key;
  if (h === "owner" || h === "ownername") return "owner";
  return null;
}

/** A cell, in the form the register stores it; `null` when it cannot be read. */
export function readCell(field: FieldKey, raw: string): string | null {
  const v = raw.trim();
  if (!v) return "";
  const type = FIELD[field].type;
  if (type === "date") {
    const iso = v.slice(0, 10);
    if (isDate(iso)) return iso;
    /* day first, as a Saudi or British Excel writes it */
    const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(v);
    if (m) {
      const d = `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
      return isDate(d) ? d : null;
    }
    return null;
  }
  if (type === "kind") {
    const k = FLEET_KINDS.find((x) => norm(x.key) === norm(v) || norm(x.label) === norm(v));
    return k ? k.key : null;
  }
  if (type === "condition") {
    const c = CONDITION_ORDER.find((x) => norm(x) === norm(v) || norm(CONDITION_LABEL[x]) === norm(v));
    return c ?? null;
  }
  if (type === "ownerKind") {
    const n = norm(v);
    if (n === "airport" || n === "theairport") return "airport";
    if (n === "stakeholder" || n === "astakeholder") return "stakeholder";
    return null;
  }
  if (type === "number") return Number.isFinite(Number(v)) ? v : null;
  return v;
}

/** A problem on one row: the field, where it is one, and a message that is also an Arabic key. */
export interface ImportIssue {
  field?: FieldKey;
  message: string;
  /** the cell as written, where the problem is that it cannot be read */
  value?: string;
}

export interface ImportRow {
  /** the line in the file, counting the heading as line 1 */
  line: number;
  tag: string;
  status: "new" | "update" | "same" | "error";
  errors: ImportIssue[];
  itemId?: string;
  flat?: Flat;
  fields: FieldChange[];
}

export interface ImportPlan {
  rows: ImportRow[];
  ignored: string[];
  /** set when the file cannot be read at all */
  problem?: string;
}

/**
 * What an import WOULD do, before anything is saved. A blank cell leaves the
 * register's value as it is — an import never erases a recorded date because
 * a spreadsheet column was empty.
 */
export function planImport(reg: FleetRegister, text: string): ImportPlan {
  const table = parseTable(text);
  if (table.length < 2)
    return { rows: [], ignored: [], problem: "The file needs a heading row and at least one item." };
  const heads = table[0];
  const cols = heads.map(columnFor);
  const ignored = heads.filter((h, i) => h.trim() && cols[i] === null);
  if (!cols.includes("tag")) return { rows: [], ignored, problem: "No tag column: every row needs the item's tag." };
  const seen = new Set<string>();
  const rows: ImportRow[] = table.slice(1).map((cells, r) => {
    const line = r + 2;
    const errors: ImportIssue[] = [];
    const given: Flat = {};
    cols.forEach((k, i) => {
      if (!k) return;
      const raw = cells[i] ?? "";
      const val = readCell(k, raw);
      if (val === null) errors.push({ field: k, message: "This cell cannot be read.", value: raw.trim() });
      else if (val !== "") given[k] = val;
    });
    const tag = (given.tag ?? "").trim();
    if (!tag) return { line, tag, status: "error", errors: [{ message: "No tag on this row." }], fields: [] };
    if (seen.has(tag.toUpperCase()))
      return {
        line,
        tag,
        status: "error",
        errors: [{ field: "tag", message: "This tag appears twice in the file." }],
        fields: [],
      };
    seen.add(tag.toUpperCase());
    const existing = reg.items.find((i) => sameTag(i.tag, tag));
    if (existing?.retired)
      return {
        line,
        tag,
        status: "error",
        errors: [{ field: "tag", message: "This item is retired. Reinstate it first." }],
        fields: [],
      };
    const base: Flat = existing ? flatten(existing) : { condition: "unknown" };
    const flat: Flat = { ...base, ...given, ...(existing ? { tag: existing.tag } : {}) };
    errors.push(...checkItem(flat, reg, existing?.id));
    const fields = diff(existing ? base : {}, flat);
    if (errors.length) return { line, tag, status: "error", errors, fields };
    return {
      line,
      tag,
      status: existing ? (fields.length ? "update" : "same") : "new",
      errors,
      itemId: existing?.id,
      flat,
      fields,
    };
  });
  return { rows, ignored };
}

/** Apply the rows that can be applied; each item's change is recorded, and the source named. */
export function applyImport(reg: FleetRegister, plan: ImportPlan, who: Actor, source: string): FleetRegister {
  let next = reg;
  for (const row of plan.rows) {
    if (!row.flat || (row.status !== "new" && row.status !== "update")) continue;
    if (row.status === "new") {
      const item = toItem(row.flat, { id: who.newId() });
      next = record(
        { ...next, items: [...next.items, item] },
        {
          id: who.newId(),
          at: who.at,
          by: who.by,
          itemId: item.id,
          tag: item.tag,
          action: "imported",
          reason: source,
          fields: row.fields,
        },
      );
    } else {
      const before = find(next, row.itemId!);
      next = record(replace(next, toItem(row.flat, before)), {
        id: who.newId(),
        at: who.at,
        by: who.by,
        itemId: before.id,
        tag: before.tag,
        action: "imported",
        reason: source,
        fields: row.fields,
      });
    }
  }
  return next;
}

/** The heading row of the import template, in the order of the form. */
export const TEMPLATE_HEADINGS = FIELDS.map((f) => f.column);

export function templateCsv(): string {
  const example = [
    "SWP-09",
    "Runway sweeper",
    "sweeper",
    "Bucher",
    "CityCat VR50",
    "2024",
    "MED-1234",
    "airport",
    "Tibah Airports Operation",
    "Airside Operations",
    "",
    "serviceable",
    "",
    "Plant yard",
    "1200",
    "Driver name",
    "ADP-0000",
    "2027-03-31",
    "2027-01-31",
    "2027-01-31",
    "AVP-0000",
    "2026-12-31",
    "2026-06-30",
    "2026-12-30",
    "pass",
  ];
  return `${TEMPLATE_HEADINGS.join(",")}\n${example.join(",")}\n`;
}
